import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const toForm = (fields, record) =>
  Object.fromEntries(fields.map(f => {
    const v = record?.[f.key];
    if (f.type === "list") return [f.key, (v || []).join("\n")];
    if (f.type === "switch") return [f.key, !!v];
    return [f.key, v ?? f.default ?? ""];
  }));

const fromForm = (fields, form) =>
  Object.fromEntries(fields.map(f => {
    const v = form[f.key];
    if (f.type === "list") return [f.key, String(v).split("\n").map(s => s.trim()).filter(Boolean)];
    if (f.type === "number") return [f.key, v === "" ? null : Number(v)];
    return [f.key, v];
  }));

const Field = ({ f, value, onChange }) => {
  const id = `field-${f.key}`;
  if (f.type === "switch") {
    return (
      <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
        <Label htmlFor={id}>{f.label}</Label>
        <Switch id={id} checked={value} onCheckedChange={onChange} data-testid={`editor-${f.key}`} />
      </div>
    );
  }
  const Comp = f.type === "textarea" || f.type === "list" ? Textarea : Input;
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between">
        <Label htmlFor={id}>{f.label}{f.required && " *"}</Label>
        {f.hint && <span className="text-xs text-slate-400">{f.hint(value)}</span>}
      </div>
      <Comp id={id} value={value} rows={f.rows || 4} type={f.type === "number" ? "number" : "text"}
        disabled={f.lockOnEdit && f._editing} onChange={e => onChange(e.target.value)} data-testid={`editor-${f.key}`}
        placeholder={f.type === "list" ? "One item per line" : f.placeholder} />
    </div>
  );
};

export const RecordEditor = ({ open, onOpenChange, title, fields, record, onSave, testid = "record-editor" }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(toForm(fields, record)); }, [open, record, fields]);

  const submit = async (e) => {
    e.preventDefault();
    const missing = fields.find(f => f.required && !String(form[f.key] ?? "").trim());
    if (missing) return;
    setSaving(true);
    try { await onSave(fromForm(fields, form)); onOpenChange(false); } catch { /* toast shown */ } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {fields.map(f => (
            <Field key={f.key} f={{ ...f, _editing: !!record }} value={form[f.key] ?? ""}
              onChange={v => setForm(p => ({ ...p, [f.key]: v }))} />
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`${testid}-save`}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
